import { useState, useEffect, useRef } from "react";
import {
  Rocket,
  Menu,
  X,
  LogOut,
  FileText,
  User,
  LogIn,
  ChevronDown,
  Settings,
} from "lucide-react";
import config from "../../config";

const ResponsiveHeader = ({
  isAuthenticated = false,
  onLogin,
  onSignup,
  onLogout,
  onCreateReport,
  onOpenSettings,
  userName = "User",
}: any) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const menuRef = useRef<any>(null);
  const profileRef = useRef<any>(null);

  // Handle clicking outside to close menus
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuRef, profileRef]);

  // Mobile menu component
  const MobileMenu = () => (
    <div
      ref={menuRef}
      className={`fixed inset-0 z-50 transform transition-transform duration-300 ease-in-out ${
        mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <div
        className="absolute inset-0 bg-gray-800 bg-opacity-50"
        onClick={() => setMobileMenuOpen(false)}
      ></div>
      <div className="absolute inset-y-0 left-0 max-w-xs w-full bg-white shadow-xl z-10 flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center">
            <Rocket className="h-6 w-6 text-purple-600 mr-2" />
            <span className="font-bold text-lg text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500">
              GoViralStrategy
            </span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-6">
          <a
            href="#how-it-works"
            className="block text-gray-700 hover:text-purple-600 transition-colors"
            onClick={() => setMobileMenuOpen(false)}
          >
            How It Works
          </a>
          <a
            href="#results"
            className="block text-gray-700 hover:text-purple-600 transition-colors"
            onClick={() => setMobileMenuOpen(false)}
          >
            Results
          </a>
          <a
            href="#pricing"
            className="block text-gray-700 hover:text-purple-600 transition-colors"
            onClick={() => setMobileMenuOpen(false)}
          >
            Pricing
          </a>
          <a
            href={config.links.legal}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-gray-700 hover:text-purple-600 transition-colors"
            onClick={() => setMobileMenuOpen(false)}
          >
            Legal
          </a>

          {isAuthenticated ? (
            <>
              <hr className="border-gray-200" />
              <div className="py-2">
                <div className="mb-4 flex items-center">
                  <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                    <User className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium">{userName}</p>
                    <p className="text-sm text-gray-500">Creator</p>
                  </div>
                </div>

                <button
                  onClick={() => {
                    onCreateReport();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full mb-3 flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <FileText className="h-5 w-5 mr-2" />
                  New Report
                </button>

                <button
                  onClick={() => {
                    onOpenSettings();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full mb-3 flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Settings className="h-5 w-5 mr-2" />
                  Settings
                </button>

                <button
                  onClick={() => {
                    onLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <LogOut className="h-5 w-5 mr-2" />
                  Log Out
                </button>
              </div>
            </>
          ) : (
            <>
              <hr className="border-gray-200" />
              <button
                onClick={() => {
                  onLogin();
                  setMobileMenuOpen(false);
                }}
                className="w-full mb-3 flex items-center justify-center px-4 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors"
              >
                <LogIn className="h-5 w-5 mr-2" />
                Log In
              </button>

              <button
                onClick={() => {
                  onSignup();
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center justify-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-lg hover:from-purple-700 hover:to-pink-600 transition-colors"
              >
                Sign Up
              </button>
            </>
          )}
        </nav>
      </div>
    </div>
  );

  // Desktop user profile dropdown
  const ProfileDropdown = () => (
    <div
      ref={profileRef}
      className={`absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50 transition-opacity duration-200 ${
        profileDropdownOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <div className="px-4 py-2 border-b border-gray-100">
        <p className="font-medium">{userName}</p>
        <p className="text-sm text-gray-500">Creator</p>
      </div>

      <button
        onClick={() => {
          onCreateReport();
          setProfileDropdownOpen(false);
        }}
        className="w-full text-left px-4 py-2 text-gray-700 hover:bg-purple-50 hover:text-purple-700 flex items-center"
      >
        <FileText className="h-4 w-4 mr-2" />
        New Report
      </button>

      <button
        onClick={() => {
          onOpenSettings();
          setProfileDropdownOpen(false);
        }}
        className="w-full text-left px-4 py-2 text-gray-700 hover:bg-purple-50 hover:text-purple-700 flex items-center"
      >
        <Settings className="h-4 w-4 mr-2" />
        Settings
      </button>

      <button
        onClick={() => {
          onLogout();
          setProfileDropdownOpen(false);
        }}
        className="w-full text-left px-4 py-2 text-gray-700 hover:bg-purple-50 hover:text-purple-700 flex items-center"
      >
        <LogOut className="h-4 w-4 mr-2" />
        Log Out
      </button>
    </div>
  );

  return (
    <>
      <header className="sticky top-0 bg-white shadow-sm z-40">
        <div className="flex items-center justify-between px-4 md:px-6 py-4 max-w-7xl mx-auto">
          {/* Mobile menu button */}
          <button
            className="md:hidden text-gray-500 hover:text-gray-700"
            onClick={() => setMobileMenuOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          {/* Logo */}
          <div className="flex items-center space-x-2">
            <Rocket className="h-6 w-6 text-purple-600" />
            <span className="font-bold text-xl text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500">
              GoViralStrategy
            </span>
          </div>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <a
              href="#how-it-works"
              className="text-gray-600 hover:text-purple-600 transition-colors"
            >
              How It Works
            </a>
            <a
              href="#results"
              className="text-gray-600 hover:text-purple-600 transition-colors"
            >
              Results
            </a>
            <a
              href="#pricing"
              className="text-gray-600 hover:text-purple-600 transition-colors"
            >
              Pricing
            </a>
            <a
              href="/legal/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-gray-700 hover:text-purple-600 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Legal
            </a>
          </div>

          {/* Authentication buttons */}
          <div className="flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <button
                  onClick={onCreateReport}
                  className="hidden md:flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <FileText className="h-5 w-5 mr-2" />
                  New Report
                </button>

                <div className="relative">
                  <button
                    onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                    className="flex items-center space-x-2 text-gray-700 hover:text-purple-600 transition-colors"
                  >
                    <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                      <User className="h-4 w-4 text-purple-600" />
                    </div>
                    <span className="hidden md:inline">{userName}</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>

                  <ProfileDropdown />
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={onLogin}
                  className="hidden md:block text-gray-700 hover:text-purple-600 transition-colors"
                >
                  Log In
                </button>

                <button
                  onClick={onSignup}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-lg hover:from-purple-700 hover:to-pink-600 transition-colors"
                >
                  Sign Up
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      <MobileMenu />
    </>
  );
};

export default ResponsiveHeader;
